/**
 * confirmDialog — blocking Promise-based replacement dla `window.confirm()`.
 *
 * **API (callable z dowolnego miejsca, w tym poza React komponentami):**
 *
 * ```ts
 * const ok = await confirmDialog({
 *   title: 'Zmiana układu',
 *   message: 'Usunie 3 okna z obszaru "Lewa kolumna"...',
 *   variant: 'warning',
 *   confirmLabel: 'Kontynuuj',
 *   cancelLabel: 'Anuluj',
 * });
 * if (ok) doAction();
 * ```
 *
 * **Architektura — singleton handler pattern:**
 *   - Funkcja `confirmDialog()` deleguje do globalnego handler'a
 *   - Handler jest rejestrowany przez `<ConfirmDialogProvider />` na mount
 *   - Provider trzyma React state, renderuje modal, resolves Promise gdy user
 *     klika OK/Cancel/Escape
 *   - Singleton pattern bo `confirmDialog()` jest callable z event handlers,
 *     async functions, server actions etc. — nie tylko z React components
 *
 * **Fallback:** jeżeli Provider nie zamontowany, fall back do `window.confirm()`
 * (defensive, nie powinno się zdarzyć w runtime).
 *
 * @see ../../components/ui/ConfirmDialog/ConfirmDialog.tsx — UI component
 * @see ../../components/ui/ConfirmDialog/ConfirmDialogProvider.tsx — Provider
 */

export type ConfirmVariant = "info" | "warning" | "danger";

export type ConfirmDialogOptions = {
  title: string;
  message: string;
  /** Kolor + ikona wariantu (default: 'warning'). */
  variant?: ConfirmVariant;
  /** Default: 'Potwierdź'. */
  confirmLabel?: string;
  /** Default: 'Anuluj'. */
  cancelLabel?: string;
};

type Handler = (opts: ConfirmDialogOptions) => Promise<boolean>;

let registeredHandler: Handler | null = null;

/**
 * Otwiera confirm dialog i zwraca Promise<boolean>.
 * - `true` — user kliknął confirm
 * - `false` — user kliknął cancel / Escape / klik poza dialog
 */
export function confirmDialog(opts: ConfirmDialogOptions): Promise<boolean> {
  if (!registeredHandler) {
    /* Defensive fallback gdy Provider nie zamontowany — np. SSR, pre-hydration,
       lub błąd w setup. Native confirm() jest synchroniczny ale Promise.resolve
       opakowuje. */
    if (typeof window === "undefined") return Promise.resolve(false);
    console.warn(
      "[confirmDialog] <ConfirmDialogProvider /> not mounted — falling back to window.confirm()",
    );
    return Promise.resolve(window.confirm(`${opts.title}\n\n${opts.message}`));
  }
  return registeredHandler(opts);
}

/* ─── Internal API używane TYLKO przez ConfirmDialogProvider ──────────── */

/** Rejestruje handler — wywoływane przez ConfirmDialogProvider w useEffect. */
export function _registerConfirmHandler(fn: Handler): () => void {
  registeredHandler = fn;
  return () => {
    if (registeredHandler === fn) registeredHandler = null;
  };
}
